import React, { useMemo } from "react";
import { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";

import { selectLayerByVersionAndLayerKey, useGetFeaturesQuery } from "fond/api/layersSlice";
import { buildPathGraph } from "fond/draw/modes/base/pathGraph";
import MultiLayerEditPanelContent from "fond/draw/MultiLayerEdit/MultiLayerEditPanelContent";
import { MultiLayerEditProvider } from "fond/draw/MultiLayerEdit/MultiLayerEditProvider";
import { validateThreePhasePath } from "fond/draw/validation/muPowerThreePhasePathValidation";
import { DeleteButton, PolygonSelectButton, RedoButton, SelectButton, ShortestPathButton, ToolbarConfig, UndoButton } from "fond/map/Toolbar";
import { MuPowerEditableLayerIds, MuPowerOutputLayerIds, MuPowerSolverLayerIds } from "fond/types";
import { isAnyLineString } from "fond/types/geojson";
import { useAppSelector } from "fond/utils/hooks";
import { BlockSpinner, useStackedNavigationContext } from "fond/widgets";

const toolbarConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [
      <SelectButton key="select" />,
      <PolygonSelectButton key="selectPolygon" />,
      <PolygonSelectButton key="selectLasso" selectionMode="lasso" />,
    ],
  },
  {
    id: "drawTools",
    type: "mode",
    items: [<ShortestPathButton key="shortestPath" />],
  },
  {
    id: "historyTools",
    type: "action",
    items: [<UndoButton key="undo" />, <RedoButton key="redo" />, <DeleteButton key="delete" />],
  },
];

const MuPowerThreePhasePathDraw: React.FC = () => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const { clear } = useStackedNavigationContext();

  const ugPathLayer = useAppSelector((state) => selectLayerByVersionAndLayerKey(state, { versionId, layerId: MuPowerEditableLayerIds.ugPath }));
  const { data: ugPathFeatures, isLoading: ugPathLoading } = useGetFeaturesQuery({ layerId: ugPathLayer?.ID ?? "" }, { skip: !ugPathLayer?.ID });

  const ugPathGraph = useMemo(() => {
    if (!ugPathFeatures) return undefined;
    return buildPathGraph(ugPathFeatures.filter(isAnyLineString), { splitIntersectingPaths: true });
  }, [ugPathFeatures]);

  const awaitingGraph = !!ugPathLayer?.ID && ugPathLoading;

  const options: MapboxDrawOptions = useMemo(
    () => ({
      snap: true,
      snapOptions: {
        snapVertexPriorityDistance: 0.000025,
      },
      shortestPath: ugPathGraph
        ? {
            graph: ugPathGraph,
            singleFeature: true,
            previewStyle: {
              path: { "line-color": "#000000", "line-width": 9 },
              tentative: { "line-color": "#000000", "line-width": 7, "line-dasharray": [1, 1] },
              node: { "circle-color": "#000000" },
            },
          }
        : undefined,
      editOptions: {
        allLayers: [
          {
            ID: MuPowerSolverLayerIds.primaryWireOverrides,
            GeometryType: "LineString",
            getFeatureProperties: () => ({ layerId: MuPowerSolverLayerIds.primaryWireOverrides, WireSize: "3x_1/0_Al" }),
            validate: validateThreePhasePath,
          },
          { ID: MuPowerSolverLayerIds.sourceHubs, GeometryType: "Point", getFeatureProperties: () => ({}), Hide: true },
          { ID: MuPowerSolverLayerIds.transformerHubs, GeometryType: "Point", getFeatureProperties: () => ({}), Hide: true },
          { ID: MuPowerOutputLayerIds.primaryWires, GeometryType: "LineString", getFeatureProperties: () => ({}), Hide: true },
          { ID: MuPowerOutputLayerIds.secondaryWires, GeometryType: "LineString", getFeatureProperties: () => ({}), Hide: true },
        ],
        editingLayerId: MuPowerSolverLayerIds.primaryWireOverrides,
        editingLayerIds: [MuPowerSolverLayerIds.primaryWireOverrides],
      },
    }),
    [ugPathGraph]
  );

  if (awaitingGraph) {
    return <BlockSpinner />;
  }

  return (
    <MultiLayerEditProvider versionId={versionId} options={options} config={toolbarConfig}>
      <MultiLayerEditPanelContent title="Edit 3-phase path" alertTitle="Draw 3-phase path" onFinish={clear} />
    </MultiLayerEditProvider>
  );
};

export default MuPowerThreePhasePathDraw;
